import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { ITipoContrato } from '../pages/eventos/components/contrato/tiposDeContrato';

/**
 * Contrato de evento: o template é do EVENTO, a ficha é de cada fornecedor.
 *
 *   evento    template(s) .docx → IA lê uma vez → mapa → uma pessoa confirma
 *   contrato  fontes → IA monta a ficha → revisão → gerar (sem IA, na hora)
 *
 * O template vem do advogado da CONTRATANTE; só o lado da contratada muda.
 */

// ── Template do evento ────────────────────────────────────────────────────

export interface IMapaCampo {
  id: string;
  papel: string;
  rotulo: string;
  grupo: string;
  paragrafo: number;
  ancora: string;
  contexto: string;
  opcional: boolean;
}

export interface IMapa {
  status: 'lendo' | 'pronto' | 'confirmado' | 'erro';
  erro?: string;
  campos?: IMapaCampo[];
  escopo_paragrafo?: number | null;
  /** A tabela de escopo do template é TEXTO: as linhas de item são remontadas. */
  escopo_tabela?: number | null;
  fica?: { contratante?: string; testemunhas?: string[]; terceiros?: string[] };
  evento?: { nome?: string; data?: string; local?: string };
  descartados?: { papel?: string; ancora?: string; motivo?: string }[];
  confirmado_por_nome?: string | null;
  confirmado_em?: string | null;
}

export interface ITemplateEvento {
  id: number;
  nome: string;
  mapa: IMapa | null;
  /** O tipo de contrato que este arquivo gera (`servico`, `produto`, `parceria_ab`...). A API anterior ao tipo
   * de contrato não manda: `comTipo` completa. */
  contrato_tipo?: string;
}

export interface ITemplateDoEvento {
  templates: ITemplateEvento[];
  /** Todos os templates do evento (de todos os tipos) confirmados. */
  confirmado: boolean;
  /** O catálogo com o estado de cada tipo no evento (a API anterior não manda). */
  tipos?: ITipoContrato[];
  evento: { nome?: string; data?: string | null; local?: string; cidade?: string; uf?: string };
}

// ── Ficha do contrato ─────────────────────────────────────────────────────

export interface ICampoFicha {
  papel: string;
  rotulo: string;
  grupo: string;
  obrigatorio: boolean;
  como: 'literal' | 'composto' | 'prosa' | 'derivado';
  /** O valor que o template tem hoje — do fornecedor anterior. */
  antes: string[];
  valor: string | null;
  fonte: string | null;
  fonte_rotulo: string | null;
  /** O trecho da fonte de onde a IA tirou o valor. */
  trecho: string | null;
  /** O sistema achou o trecho na fonte e o valor dentro dele (sem IA). */
  conferido: boolean | null;
  motivo: string | null;
}

export interface IItemEscopo {
  descricao: string;
  quantidade?: string;
  valor?: string;
}

export interface IGateItem {
  tipo: string;
  campo: string;
  detalhe: string;
  template?: string;
}

/** O documento para assinatura (contrato + anexo + orçamento) ou por que não saiu. */
export interface IPacote {
  anexo_id?: number | null;
  nome_arquivo?: string;
  gerado_em?: string;
  aprovado: boolean;
  erro?: string;
  /** O orçamento (ou o tipo do contrato) mudou depois deste documento: o envio fica barrado até refazer. */
  desatualizado?: boolean;
  /** 'tipo': o tipo do contrato mudou depois de gerar — refazer não basta, o contrato é gerado de novo. */
  desatualizado_por?: string;
  /** Word corrigido do documento inteiro: vale o veredito dele; refazer o documento descarta. */
  revisado_em?: string | null;
  revisado_por_nome?: string | null;
  bloqueios?: number;
  liberado_em?: string | null;
  liberado_por_nome?: string | null;
  bloqueios_liberados?: IGateItem[];
}

/** Qual documento (posição no gate) e a versão (anexo) que a tela mostrava: se mudou, 409. */
export interface IVersaoDocumento {
  indice: number;
  anexoId?: number | null;
}

export interface IResultado {
  gerado_em: string | null;
  aprovado: boolean | null;
  bloqueios: IGateItem[];
  conferir: IGateItem[];
  documentos: {
    template_nome: string;
    aprovado: boolean;
    bloqueios: number;
    anexo_id?: number | null;
    nome_arquivo?: string;
    /** Word corrigido reenviado pelo jurídico. */
    revisado_em?: string | null;
    revisado_por_nome?: string | null;
    /** Envio liberado mesmo com bloqueio do gate (só Word corrigido). */
    liberado_em?: string | null;
    liberado_por_nome?: string | null;
    bloqueios_liberados?: IGateItem[];
  }[];
  pacote?: IPacote | null;
}

/**
 * Seção do negócio (parceria, bar, produto): o trecho do modelo que muda de estrutura a cada proposta. A IA redige na
 * leitura das fontes; o operador pode reescrever; o gerar troca o texto do modelo por estes parágrafos.
 */
export interface ISecaoFicha {
  /** "<template>:<campo>" — a chave do PUT da ficha. */
  chave: string;
  titulo: string;
  template: string;
  /** O texto do fornecedor anterior, parágrafo a parágrafo. */
  modelo: string[];
  paragrafos: { texto: string; molde: number }[];
  fonte: string | null;
  fonte_rotulo: string | null;
  conferido: boolean | null;
  motivo: string | null;
  avisos: string[];
  /** Na seção que o operador escreveu: número que não está nas fontes (não barra; o gate pede conferência). */
  numeros_sem_fonte?: string[];
  /** A proposta não tem esta parte do negócio (sem adiantamento, sem open bar): a seção sai do contrato. */
  removida?: boolean;
}

export interface IFicha {
  kanban_card_id: number | null;
  fornecedor_nome: string;
  /** O tipo de contrato: só os templates dele entram (e contam para o `pronto`). */
  contrato_tipo?: string;
  templates: { id: number; nome: string; status: IMapa['status'] | null }[];
  /** Todos os templates do TIPO deste contrato lidos e confirmados. */
  pronto: boolean;
  fontes: {
    texto: string;
    cnpj: string;
    documentos: { id: number; nome: string; tipo: string }[];
  };
  campos: ICampoFicha[];
  /** Back sem tipo de contrato (antes de 30/09) não manda. */
  secoes?: ISecaoFicha[];
  tem_escopo: boolean;
  itens: IItemEscopo[];
  avisos: string[];
  lido_em: string | null;
  resultado: IResultado;
}

export const chaveFicha = (eventoId: number, contratoId: number) =>
  ['evento-contrato-ficha', eventoId, contratoId] as const;
export const chaveTemplateDoEvento = (eventoId: number) =>
  ['evento-template-contrato', eventoId] as const;

export function useFicha(eventoId: number | null, contratoId: number | null) {
  return useQuery<{ data: IFicha }>({
    queryKey: chaveFicha(eventoId as number, contratoId as number),
    queryFn: () =>
      api.get(`/api/v1/eventos/${eventoId}/contratos/${contratoId}/ficha`).then(r => r.data),
    enabled: !!eventoId && !!contratoId,
  });
}

export function useTemplateDoEvento(eventoId: number | null, habilitado = true) {
  return useQuery<{ data: ITemplateDoEvento }>({
    queryKey: chaveTemplateDoEvento(eventoId as number),
    queryFn: () => api.get(`/api/v1/eventos/${eventoId}/template-contrato`).then(r => r.data),
    enabled: !!eventoId && habilitado,
    // Enquanto algum template está sendo lido, o SERVIDOR diz quando acabou. Só pela
    // tarefa, fechar e reabrir o drawer (ou dois envios seguidos) perdia o fim da
    // leitura e o template ficava "lendo…" sem ação.
    refetchInterval: q =>
      q.state.data?.data?.templates?.some(t => t.mapa?.status === 'lendo') ? 3000 : false,
  });
}

/** Leitura de IA leva ~1 min; passado disso a tarefa se perdeu (API reiniciada). */
const TAREFA_MAXIMO_MS = 5 * 60 * 1000;

/**
 * Acompanha uma tarefa de IA em background até terminar (~30s a ~1 min).
 *
 * Polling em `/tarefas/{id}`, e não WebSocket: é uma tela só esperando uma
 * resposta só, e o endpoint genérico de tarefas já existe.
 *
 * `rodando` só cai depois que o `onFim` termina — ele recarrega a tela, e liberar
 * antes deixava digitar num campo que o recarregamento em seguida apagava. Se a
 * API reinicia no meio, o status fica "processing" no Redis por 1 h: depois de
 * 5 min a tarefa é dada como perdida, em vez de travar a tela.
 */
export function useTarefa(
  onFim: (status: 'completed' | 'failed', erro?: string) => void | Promise<unknown>,
) {
  const [taskId, setTaskId] = useState<string | null>(null);
  const inicio = useRef(0);
  const terminando = useRef<string | null>(null);
  const { data, dataUpdatedAt } = useQuery<{ data: { status: string; error?: string } }>({
    queryKey: ['evento-tarefa', taskId],
    queryFn: () => api.get(`/api/v1/eventos/tarefas/${taskId}`).then(r => r.data),
    enabled: !!taskId,
    refetchInterval: 2000,
  });
  const status = data?.data?.status;
  useEffect(() => {
    if (!taskId || terminando.current === taskId) return;
    const acabou = status === 'completed' || status === 'failed' || status === 'not_found';
    const perdida = !acabou && Date.now() - inicio.current > TAREFA_MAXIMO_MS;
    if (!acabou && !perdida) return;
    terminando.current = taskId;
    Promise.resolve(
      onFim(
        status === 'completed' ? 'completed' : 'failed',
        perdida ? 'A leitura não terminou. Tente de novo.' : data?.data?.error,
      ),
    ).finally(() => setTaskId(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, taskId, dataUpdatedAt]);
  const acompanhar = (id: string) => {
    inicio.current = Date.now();
    terminando.current = null;
    setTaskId(id);
  };
  return { acompanhar, rodando: !!taskId };
}

export function useAcoesContrato(eventoId: number | null, contratoId: number | null) {
  const queryClient = useQueryClient();
  const base = `/api/v1/eventos/${eventoId}/contratos/${contratoId}`;
  const invalidar = () =>
    queryClient.invalidateQueries({
      queryKey: chaveFicha(eventoId as number, contratoId as number),
    });

  const salvar = useMutation({
    mutationFn: (dados: {
      texto?: string;
      cnpj?: string;
      valores?: Record<string, string | null>;
      itens?: IItemEscopo[];
      /** Seções reescritas: chave -> parágrafos. */
      secoes?: Record<string, string[]>;
    }) => api.put(`${base}/ficha`, dados).then(r => r.data),
    onSuccess: invalidar,
  });

  const lerFontes = useMutation({
    mutationFn: () => api.post(`${base}/ler-fontes`).then(r => r.data?.data?.task_id as string),
  });

  const gerar = useMutation({
    mutationFn: () => api.post(`${base}/gerar`).then(r => r.data),
    onSuccess: invalidar,
  });

  // Monta de novo só o documento para assinatura, com os atuais (o Word corrigido fica).
  // Recarrega tambem no erro: com 409 (versao velha, envio pendente) a tela se atualiza.
  const refazerDocumento = useMutation({
    mutationFn: () => api.post(`${base}/pacote`).then(r => r.data),
    onSettled: invalidar,
  });

  // Word corrigido pelo jurídico: o servidor confere de novo e refaz o documento para assinatura.
  const enviarRevisado = useMutation({
    mutationFn: ({ indice, anexoId, arquivo }: IVersaoDocumento & { arquivo: File }) => {
      const dados = new FormData();
      dados.append('file', arquivo);
      if (anexoId) dados.append('anexo_id', String(anexoId));
      return api
        .post(`${base}/documentos/${indice}/revisado`, dados, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(r => r.data);
    },
    onSettled: invalidar,
  });

  const liberar = useMutation({
    mutationFn: ({ indice, anexoId }: IVersaoDocumento) =>
      api
        .post(`${base}/documentos/${indice}/liberar`, null, { params: { anexo_id: anexoId } })
        .then(r => r.data),
    onSettled: invalidar,
  });

  // O mesmo no documento para assinatura inteiro (contrato + anexo + orçamento).
  const enviarRevisadoPacote = useMutation({
    mutationFn: ({ anexoId, arquivo }: { anexoId?: number | null; arquivo: File }) => {
      const dados = new FormData();
      dados.append('file', arquivo);
      if (anexoId) dados.append('anexo_id', String(anexoId));
      return api
        .post(`${base}/pacote/revisado`, dados, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(r => r.data);
    },
    onSettled: invalidar,
  });

  const liberarPacote = useMutation({
    mutationFn: (anexoId?: number | null) =>
      api.post(`${base}/pacote/liberar`, null, { params: { anexo_id: anexoId } }).then(r => r.data),
    onSettled: invalidar,
  });

  return {
    salvar,
    lerFontes,
    gerar,
    refazerDocumento,
    enviarRevisado,
    liberar,
    enviarRevisadoPacote,
    liberarPacote,
    invalidar,
  };
}

export function useAcoesTemplate(eventoId: number | null) {
  const queryClient = useQueryClient();
  const base = `/api/v1/eventos/${eventoId}/template-contrato`;
  const invalidar = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: chaveTemplateDoEvento(eventoId as number) }),
      queryClient.invalidateQueries({ queryKey: ['evento-contrato-ficha', eventoId] }),
      queryClient.invalidateQueries({ queryKey: ['card-contrato'] }),
    ]);

  const ler = useMutation({
    mutationFn: (docId: number) =>
      api.post(`${base}/${docId}/ler`).then(r => r.data?.data?.task_id as string),
    // Mesmo quando falha: o status do template pode ter mudado no servidor.
    onSettled: invalidar,
  });

  const confirmar = useMutation({
    mutationFn: ({ docId, campos }: { docId: number; campos: string[] }) =>
      api.put(`${base}/${docId}/mapa`, { campos, confirmar: true }).then(r => r.data),
    onSuccess: invalidar,
  });

  const remover = useMutation({
    mutationFn: (docId: number) =>
      api.delete(`/api/v1/eventos/${eventoId}/documentos/${docId}`).then(r => r.data),
    onSuccess: invalidar,
  });

  /**
   * O template subiu no tipo errado. A leitura é por tipo: o mapa lido como outro tipo sai e o
   * back responde `ler_de_novo` (a tela confirma antes, quando o template já tem mapa).
   */
  const trocarTipo = useMutation({
    mutationFn: ({ docId, contratoTipo }: { docId: number; contratoTipo: string }) =>
      api.put(`${base}/${docId}/tipo`, { contrato_tipo: contratoTipo }).then(r => r.data),
    onSettled: invalidar,
  });

  return { ler, confirmar, remover, trocarTipo, invalidar };
}

export function mensagemDoErro(err: unknown, padrao: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : padrao;
}
